import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  CancelToken,
} from "axios";
import useEvent from "react-use-event-hook";

import {
  checkResponseStatus,
  errors,
  getAccessToken,
  IApiError,
  Log,
  RequestBodyModel,
  RequestHeadersModel,
  showTokenExpiredAlert,
  TApiResponse,
  TApiSuccess,
} from "@ryde/shared";

import { useLandingsNavigation } from "../hooks";
import {
  getLandingApiUrl,
  handleApiLandingException,
} from "./api-landing-utils";

export const useLandingApiMethods = () => {
  const { navigateLogin } = useLandingsNavigation();

  const doApiLandingCall = useEvent(
    async <T>(
      axiosConfig: AxiosRequestConfig<RequestBodyModel>,
    ): Promise<TApiSuccess<T> | undefined> => {
      try {
        const response = (await axios(axiosConfig)) as AxiosResponse<
          TApiResponse<T>
        >;
        return await checkResponseStatus(response);
      } catch (e) {
        Log.debug("Response axios exception ", e);
        let isExceptionHandled = false;

        try {
          const response = await handleApiLandingException<T>({
            e: e as AxiosError<IApiError, unknown>,
          });

          if (response) {
            Log.debug("Response after handling api error", response);
            return await checkResponseStatus(response);
          }
        } catch (apiError) {
          Log.debug(
            "Second-level api error from handleApiException: ",
            apiError,
          );
          const handleApiErrorTyped = apiError as IApiError;

          if (
            handleApiErrorTyped.translationKey ===
              errors.auth.tokenExpiredError ||
            handleApiErrorTyped.translationKey ===
              errors.common.authenticationError
          ) {
            isExceptionHandled = true;
            Log.debug(
              "Api authentication error handled, redirecting to login page ",
              apiError,
              handleApiErrorTyped.translationKey,
            );
            showTokenExpiredAlert();

            navigateLogin();
          } else {
            throw apiError;
          }
        }

        if (!isExceptionHandled) {
          throw e;
        }

        return undefined;
      }
    },
  );

  const apiGet = useEvent(
    async <T>(
      url: string,
      body?: RequestBodyModel,
      controller?: AbortController,
      headers?: RequestHeadersModel,
      authorize = true,
      cancelToken?: CancelToken,
    ): Promise<TApiSuccess<T> | undefined> => {
      let query = "";

      if (body) {
        query = "?" + new URLSearchParams(body).toString();
        Log.debug("Get request with query", query);
      }

      const requestHeaders: RequestHeadersModel = {
        Accept: "application/json",
        ...headers,
      };

      if (authorize) {
        const token = getAccessToken();
        requestHeaders.Authorization = token ? `Bearer ${token}` : "";
      }

      const axiosConfig: AxiosRequestConfig<RequestBodyModel> = {
        baseURL: getLandingApiUrl(),
        url: url + query,
        method: "GET",
        headers: requestHeaders,
        signal: controller?.signal,
        cancelToken,
      };

      return doApiLandingCall(axiosConfig);
    },
  );

  const apiPost = useEvent(
    async <T>(
      url: string,
      body?: RequestBodyModel,
      controller?: AbortController,
      headers?: RequestHeadersModel,
      authorize = true,
      cancelToken?: CancelToken,
      responseType?: "json" | "text" | "blob",
    ): Promise<TApiSuccess<T> | undefined> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const requestHeaders: Record<string, any> = {
        Accept: "application/json",
        ...headers,
      };

      if (authorize) {
        const token = getAccessToken();
        requestHeaders.Authorization = token ? `Bearer ${token}` : "";
      }

      const axiosConfig: AxiosRequestConfig<RequestBodyModel> = {
        baseURL: getLandingApiUrl(),
        url: url,
        data: body,
        method: "POST",
        headers: requestHeaders,
        signal: controller?.signal,
        cancelToken,
      };

      if (responseType) {
        axiosConfig.responseType = responseType;
      }

      return doApiLandingCall(axiosConfig);
    },
  );

  const apiPostExtended = useEvent(
    async <T>({
      url,
      body,
      abortController,
      headers,
      authorize = true,
      cancelToken,
      responseType,
    }: {
      url: string;
      body?: RequestBodyModel;
      abortController?: AbortController;
      headers?: RequestHeadersModel;
      authorize?: boolean;
      cancelToken?: CancelToken;
      responseType?: "json" | "text" | "blob";
    }): Promise<TApiSuccess<T> | undefined> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const requestHeaders: Record<string, any> = {
        Accept: "application/json",
        ...headers,
      };

      if (authorize) {
        const token = getAccessToken();
        requestHeaders.Authorization = token ? `Bearer ${token}` : "";
      }

      const axiosConfig: AxiosRequestConfig<RequestBodyModel> = {
        baseURL: getLandingApiUrl(),
        url: url,
        data: body,
        method: "POST",
        headers: requestHeaders,
        signal: abortController?.signal,
        cancelToken,
      };

      if (responseType) {
        axiosConfig.responseType = responseType;
      }

      return doApiLandingCall(axiosConfig);
    },
  );

  const apiPatch = useEvent(
    async <T>(
      url: string,
      body?: RequestBodyModel,
      controller?: AbortController,
      headers?: RequestHeadersModel,
      authorize = true,
      cancelToken?: CancelToken,
    ): Promise<TApiSuccess<T> | undefined> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const requestHeaders: Record<string, any> = {
        Accept: "application/json",
        ...headers,
      };

      if (authorize) {
        const token = getAccessToken();
        requestHeaders.Authorization = token ? `Bearer ${token}` : "";
      }

      const axiosConfig: AxiosRequestConfig<RequestBodyModel> = {
        baseURL: getLandingApiUrl(),
        url: url,
        data: body,
        method: "PATCH",
        headers: requestHeaders,
        signal: controller?.signal,
        cancelToken,
      };

      return doApiLandingCall(axiosConfig);
    },
  );

  const apiDelete = useEvent(
    <T>(
      url: string,
      body?: RequestBodyModel,
      controller?: AbortController,
      headers?: RequestHeadersModel,
      authorize = true,
      cancelToken?: CancelToken,
    ): Promise<TApiSuccess<T> | undefined> => {
      const requestHeaders: RequestHeadersModel = {
        Accept: "application/json",
        ...headers,
      };

      if (authorize) {
        const token = getAccessToken();
        requestHeaders.Authorization = token ? `Bearer ${token}` : "";
      }

      const axiosConfig: AxiosRequestConfig<RequestBodyModel> = {
        baseURL: getLandingApiUrl(),
        url: url,
        data: body,
        method: "DELETE",
        headers: requestHeaders,
        signal: controller?.signal,
        cancelToken,
      };

      return doApiLandingCall(axiosConfig);
    },
  );

  return {
    apiGet,
    apiPostExtended,
    apiPost,
    apiPatch,
    apiDelete,
    doApiLandingCall,
  };
};
